import { canvasSteps } from '@bb/contracts';

/**
 * Ports of the step types the inspector edits, as the runtime handlers declare them (`STEP_CATALOG` in `@bb/domain`):
 * the dashboard does not depend on the domain package, so the rules are mirrored here (fixed ports from
 * `canvasSteps` of `@bb/contracts`) and checked by the draft validation (`ports_mismatch`) on the server.
 */

/** Port of the users no paths group took; always the last branch. */
export const EVERYONE_ELSE = 'everyone_else';

/** Paths steps whose branch order a running Canvas locks (`ORDER_LOCKED_STEP_TYPES`, BB-121). */
export const ORDER_LOCKED_TYPES: ReadonlySet<string> = new Set(['action_paths', 'audience_paths', 'experiment_paths']);

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Ids of the groups without `exitAfter`, then `everyone_else` unless it exits (Action Paths, Audience Paths). */
export function pathsPorts(config: unknown): string[] {
  const c = isObject(config) ? config : {};
  const groups = Array.isArray(c.groups) ? c.groups.filter(isObject) : [];
  const everyoneElse = isObject(c.everyoneElse) ? c.everyoneElse : {};
  return [...groups.filter((g) => !g.exitAfter && typeof g.id === 'string').map((g) => g.id as string), ...(everyoneElse.exitAfter ? [] : [EVERYONE_ELSE])];
}

/** The ports a config gives; `undefined` for types the inspector does not edit (their links stay as they are). */
export function portsOf(type: string, config: unknown): string[] | undefined {
  switch (type) {
    case 'message':
    case 'delay':
      return ['next'];
    case 'context':
      return [canvasSteps.CONTEXT_NEXT];
    case 'user_update':
      return [canvasSteps.USER_UPDATE_NEXT];
    case 'decision_split':
      return [...canvasSteps.DECISION_SPLIT_PORTS];
    case 'action_paths':
    case 'audience_paths':
      return pathsPorts(config);
    default:
      return undefined;
  }
}

/** A step of the running version whose branch order cannot change (same id and type there, a paths type). */
export function isOrderLocked(published: { steps: Record<string, { type: string }> } | null | undefined, stepId: string, type: string): boolean {
  return Boolean(published && ORDER_LOCKED_TYPES.has(type) && published.steps[stepId]?.type === type);
}
